import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";

const Add = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { shop } = useTenant();
  const selectedBranch = auth?.user?.selected_branch
    ? String(auth.user.selected_branch)
    : auth?.user?.user?.selected_branch
      ? String(auth.user.user.selected_branch)
      : "";
  const tenantShopId = shop?.id ? String(shop.id) : "";
  const { handleAdd, loading, resetForm } = useAdd("shop/categories", token);
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const shops = shopsData?.results || [];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedShop, setSelectedShop] = useState(selectedBranch || tenantShopId || "");

  const resolvedSelectedShop =
    selectedShop || selectedBranch || tenantShopId || (shops.length ? String(shops[0].id) : "");

  useEffect(() => {
    if (shopLoading || !shops?.length) return;
    const branchShop = selectedBranch
      ? shops.find((shop) => String(shop.id) === selectedBranch)
      : null;
    const tenantMatchedShop = tenantShopId
      ? shops.find((currentShop) => String(currentShop.id) === tenantShopId)
      : null;
    if (branchShop) {
      setSelectedShop(String(branchShop.id));
    } else if (tenantMatchedShop) {
      setSelectedShop(String(tenantMatchedShop.id));
    } else if (shops.length >= 1) {
      setSelectedShop(String(shops[0].id));
    }
  }, [shopLoading, shops, selectedBranch, tenantShopId]);

  const handleSubmit = () => {
    const formData = {
      name: name.trim(),
      description,
      shop: resolvedSelectedShop,
    };
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setName("");
      setDescription("");
      const branchShop = selectedBranch
        ? shops.find((shop) => String(shop.id) === selectedBranch)
        : null;
      if (branchShop) {
        setSelectedShop(String(branchShop.id));
      } else if (tenantShopId) {
        setSelectedShop(String(tenantShopId));
      } else if (shops && shops.length === 1) {
        setSelectedShop(String(shops[0].id));
      } else if (shops && shops.length > 1) {
        setSelectedShop(String(shops[0].id));
      } else {
        setSelectedShop("");
      }
      onClose();
    }
  }, [resetForm, onClose, shops, selectedBranch, tenantShopId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("category.addCategory")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("category.categoryName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("category.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("category.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || !resolvedSelectedShop}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("category.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Add;
